import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputTextComponent } from '../input-text/input-text.component';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-tags-input',
  templateUrl: './tags-input.component.html',
  styleUrls: ['./tags-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagsInputComponent),
      multi: true,
    },
  ],
})
export class TagsInputComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() placeholder: string = 'Add a tag...';
  @Input() maxTags: number = 50;
  @Input() maxLength: number = 50;
  @Input() allowDuplicates: boolean = false;
  @Input() label: string = '';
  @Input() required: boolean = false;

  @Output() tagAdded = new EventEmitter<string>();
  @Output() tagRemoved = new EventEmitter<string>();

  /** The composed in-chip base primitive; its `bareInput` is the native input. */
  @ViewChild(InputTextComponent) private tagInput?: InputTextComponent;

  tags: string[] = [];
  inputValue: string = '';
  disabled: boolean = false;
  focused: boolean = false;
  private destroy$ = new Subject<void>();

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string[]): void {
    this.tags = value || [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /** Mirrors the former [(ngModel)] binding on the raw input. */
  onInputChange(event: Event): void {
    this.inputValue = (event.target as HTMLInputElement).value;
  }

  onInputKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    // Handle Enter, Comma, or Tab to add tag
    if (
      (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') &&
      value
    ) {
      event.preventDefault();
      this.addTag(value);
      input.value = '';
      this.inputValue = '';
    }

    // Handle Backspace to remove last tag when input is empty
    if (event.key === 'Backspace' && !value && this.tags.length > 0) {
      event.preventDefault();
      this.removeTag(this.tags.length - 1);
    }
  }

  onInputBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (value) {
      this.addTag(value);
      input.value = '';
      this.inputValue = '';
    }

    this.focused = false;
    this.onTouched();
  }

  onInputFocus(): void {
    this.focused = true;
  }

  addTag(tag: string): void {
    const trimmedTag = tag.trim();

    // Validate tag
    if (!trimmedTag) {
      return;
    }

    // Check max length
    if (trimmedTag.length > this.maxLength) {
      return;
    }

    // Check duplicates (case-insensitive)
    const existingTag = this.tags.find(
      (existingTag) => existingTag.toLowerCase() === trimmedTag.toLowerCase(),
    );

    if (!this.allowDuplicates && existingTag) {
      return;
    }

    // Check max tags
    if (this.tags.length >= this.maxTags) {
      return;
    }

    this.tags = [...this.tags, trimmedTag];
    this.onChange(this.tags);

    // Emit event for tag addition
    this.tagAdded.emit(trimmedTag);
  }

  removeTag(index: number): void {
    if (this.disabled) {
      return;
    }

    const removedTag = this.tags[index];
    this.tags = this.tags.filter((_, i) => i !== index);
    this.onChange(this.tags);

    // Emit event for tag removal
    this.tagRemoved.emit(removedTag);
  }

  onContainerClick(): void {
    if (!this.disabled) {
      this.tagInput?.bareInput?.nativeElement.focus();
    }
  }
}
